import { FilmTile } from "@components/film/FilmTile"
import { NewsTile } from "@components/news/NewsTile"
import { Button } from "@components/ui/Button"
import { useProgramFilms } from "@hooks/queries"
import { filmsWithSessions } from "@hooks/useFilmSearch"
import { tv } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { Link } from "eddev/routing"
import { useMemo } from "react"
import { EventType } from "../../types.graphql"
import { useRPCQuery } from "eddev/hooks"

export const meta: BlockMeta = {
  title: "Related Films",
  icon: "video-alt3",
  tags: ["film-root"],
  flags: {
    fullWidth: true,
  },
  conditions: {
    postTypes: ["film"],
  },
}

export default defineBlock("film/film-related", (props) => {
  const styles = createStyles()

  const programFilms = useRPCQuery("api.program.films", { program: props.film?.program?.id! })
  const programFilmsItems = programFilms.data?.program?.films ?? []
  const items = useMemo(() => {
    let items = props.filmQuery?.items ?? filmsWithSessions(programFilmsItems) ?? []

    items = items.filter((film) =>
      [EventType.Film, EventType.MultiFilm, EventType.Event, EventType.Other].includes(
        film?.film?.eventInfo?.eventType!,
      ),
    )

    // don't show current film
    items = items.filter((item) => item.film?.id !== props.film?.id)

    // filter multiple sessions
    items = items.filter((v, i, a) => v.film.title && a.findIndex((t) => t.film.title === v.film.title) === i)

    // filter locally
    const collections = items.filter((item) => {
      return props.film?.collections?.some((collection) => {
        return item.film?.collections?.some((c) => c?.id === collection?.id)
      })
    })
    const categories = items.filter((item) => {
      return item.film?.filmInfo?.category === props.film?.filmInfo?.category
    })
    const genres = items.filter((item) => {
      return item.film?.filmInfo?.genre === props.film?.filmInfo?.genre
    })
    const themes = items.filter((item) => {
      return item.film?.filmInfo?.theme === props.film?.filmInfo?.theme
    })

    // shuffle each randomly
    collections.sort(() => Math.random() - 0.5)
    categories.sort(() => Math.random() - 0.5)
    genres.sort(() => Math.random() - 0.5)
    themes.sort(() => Math.random() - 0.5)

    const all = [...collections, ...categories, ...genres, ...themes]

    // filter duplicates by id
    items = all.filter((item, ix, self) => ix === self.findIndex((t) => t.film?.id === item.film?.id))

    // only show 4 max
    items = items.slice(0, 4)

    return items
  }, [programFilms.data])

  return (
    (env.admin || items.length > 0) && (
      <div className={styles.wrapper()}>
        {/* Heading/CTA */}
        <div className="flex justify-between items-center mb-7 md:mb-4">
          <EditableText
            store="title"
            as="h4"
            className="type-title-s uppercase"
            placeholder="Related Films & Events"
            defaultValue="Related Films & Events"
          />
          <Button href="/browse" variant="link">
            <EditableText store="cta" as="span" placeholder="View all" defaultValue="View all" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-7 md:gap-4">
          {items.map((item, i) => (
            <div key={i} className={styles.item()}>
              <FilmTile film={item.film} session={undefined} />
            </div>
          ))}
        </div>
      </div>
    )
  )
})

const createStyles = tv({
  slots: {
    wrapper: "flex flex-col p-4 my-10",
    item: "col-span-1",
  },
})
